import { useMemo } from 'react';
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { billingKeys, teamMembersKeys, teamsKeys, teamTrialsKeys } from '@data/constants';
import {
  CheckoutView,
  CustomerPortalView,
  ListTeamTrialView,
  TeamBody,
  TeamDetailsView,
  TeamMemberBody,
  TeamMemberListView,
  TeamMemberView,
  TeamUpdateBody,
  TeamView,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';
import { APIError } from '@data/utils/types';

export const useCreateNewTeam = (): UseMutationResult<TeamView, APIError, TeamBody, unknown> => {
  return useMutation(async (values) => {
    const response = await MatchingApi.team.createTeam({ teamBody: values });
    return response.data;
  });
};

export const useUpdateTeamMembership = (): UseMutationResult<
  TeamMemberView,
  any,
  { teamId: string; userId: string; values: TeamMemberBody },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ teamId, userId, values }) => {
      const response = await MatchingApi.team.updateTeamMembership({
        id: teamId,
        userId,
        teamMemberBody: values,
      });
      return response.data;
    },
    {
      onMutate: ({ teamId, userId, values }) => {
        const previousTeamMembers = queryClient.getQueryData<InfiniteData<TeamMemberListView>>(
          teamMembersKeys.list(teamId),
        );

        if (previousTeamMembers) {
          const newPages = previousTeamMembers.pages.map((page: { items: TeamMemberView[] }) => ({
            ...page,
            items: page.items.map((user) =>
              user.userId === userId
                ? {
                    ...user,
                    teamPermissions: values.teamPermissions,
                    trialPermissions: values.trialPermissions,
                    teamRole: values.teamRole,
                  }
                : user,
            ),
          }));

          queryClient.setQueryData(teamMembersKeys.list(teamId), {
            ...previousTeamMembers,
            pages: newPages,
          });
        }
        return { previousTeamMembers };
      },
      onError: (_, { teamId }, context) => {
        queryClient.setQueryData(teamMembersKeys.list(teamId), context?.previousTeamMembers);
      },
    },
  );
};

export const useTeamDetails = (teamId: string) =>
  useQuery<TeamDetailsView, Error>(
    teamsKeys.detail(teamId),
    async () => {
      const response = await MatchingApi.team.getTeamDetails({ id: teamId });
      return response.data;
    },
    { enabled: !!teamId },
  );

export const useUpdateTeam = (): UseMutationResult<
  TeamView,
  APIError,
  { teamId: string; values: TeamUpdateBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ teamId, values }) => {
      const response = await MatchingApi.team.updateTeam({ id: teamId, teamUpdateBody: values });
      return response.data;
    },
    { onSuccess: (_, { teamId }) => queryClient.invalidateQueries(teamsKeys.detail(teamId)) },
  );
};

export const useTeamMembers = (teamId: string) => {
  const params = { limit: 20 };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useInfiniteQuery<
    TeamMemberListView,
    Error
  >({
    queryKey: teamMembersKeys.list(teamId),
    queryFn: async ({ pageParam = params }) => {
      const response = await MatchingApi.team.getTeamMembers({
        id: teamId,
        ...pageParam,
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextPageToken
        ? {
            limit: params.limit,
            nextPageToken: lastPage.nextPageToken,
          }
        : undefined;
    },

    keepPreviousData: true,
    refetchOnWindowFocus: false,
    enabled: !!teamId,
  });

  const teamMembers = data?.pages.flatMap((page) => page.items) || [];

  const teamMembersOptions = useMemo(
    () =>
      teamMembers.map((member) => ({
        label: `${member.firstName} ${member.lastName}`,
        value: member.userId,
      })),
    [teamMembers],
  );

  return {
    teamMembers,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    teamMembersOptions,
  };
};

export const teamMembersCountQuery = ({ teamId }: { teamId: string }) => ({
  queryKey: teamMembersKeys.listCount(teamId),
  queryFn: async () => {
    const response = await MatchingApi.team.getTeamMembers({ id: teamId, limit: 2 });
    return response.data;
  },
  enabled: !!teamId,
});

// Billing
export const useCheckoutSession = (id: string, enabled: boolean) =>
  useQuery<CheckoutView, APIError>(
    billingKeys.checkoutSession(id),
    async () => {
      const response = await MatchingApi.team.getCheckoutSession({ id });
      return response.data;
    },
    { enabled: !!id && enabled },
  );

export const useCustomerPortalUrl = (id: string, enabled: boolean) =>
  useQuery<CustomerPortalView, APIError>(
    billingKeys.customerPortalURL(id),
    async () => {
      const response = await MatchingApi.team.getCustomerPortalURL({ id });
      return response.data;
    },
    { enabled: !!id && enabled },
  );

export const useTeamTrials = (teamId: string) => {
  const { data, isLoading } = useQuery<ListTeamTrialView, APIError>(
    teamTrialsKeys.list(teamId),
    async () => {
      const response = await MatchingApi.team.getTeamTrials({ id: teamId });
      return response.data;
    },
    { enabled: !!teamId },
  );

  return { data, isLoading };
};
